<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">
      <b-card class="mb-0">
        <b-link class="brand-logo">
          <vuexy-logo style="height: 4em;" />
        </b-link>

        <b-overlay :show="show" rounded="sm" opacity="0.2" no-fade>
          <!-- checking link -->
          <div v-if="tokenState === 'checking'" class="text-center py-2">
            <b-spinner variant="primary" />
          </div>

          <!-- invalid / expired link -->
          <div v-else-if="tokenState === 'invalid'">
            <b-card class="bg-light-danger mb-2 text-center">
              <small>
                This reset link is invalid or has expired. Reset links can only be used once.
              </small>
            </b-card>
            <b-button variant="primary" block to="/password-reset">
              Request a new link
            </b-button>
            <div class="mt-1 mb-1 d-flex align-items-center justify-content-center">
              <a href="/login"> Back to login </a>
            </div>
          </div>

          <!-- new password form -->
          <validation-observer
            v-else
            ref="resetValidation"
            #default="{ invalid }"
          >
            <b-card class="bg-light-secondary mb-2 text-center">
              <small>Choose a new password for your account.</small>
            </b-card>

            <b-form class="auth-login-form mt-2" @submit.prevent="submit">
              <b-form-group label-for="new-password" label="New Password">
                <validation-provider
                  #default="{ errors }"
                  name="New Password"
                  vid="password"
                  rules="required|min:8"
                >
                  <b-input-group class="input-group-merge">
                    <b-form-input
                      id="new-password"
                      v-model="password"
                      :type="passwordFieldType"
                      :state="errors.length > 0 ? false : null"
                      autocomplete="new-password"
                      autofocus
                    />
                    <b-input-group-append is-text>
                      <feather-icon
                        :icon="passwordToggleIcon"
                        class="cursor-pointer"
                        @click="togglePasswordVisibility"
                      />
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-form-group label-for="confirm-password" label="Confirm New Password">
                <validation-provider
                  #default="{ errors }"
                  name="Confirm New Password"
                  rules="required|confirmed:password"
                >
                  <b-form-input
                    id="confirm-password"
                    v-model="passwordConfirmation"
                    :type="passwordFieldType"
                    :state="errors.length > 0 ? false : null"
                    autocomplete="new-password"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-button variant="primary" type="submit" block :disabled="invalid">
                Set New Password
              </b-button>

              <div class="mt-1 mb-1 d-flex align-items-center justify-content-center">
                <a href="/login"> Back to login </a>
              </div>
            </b-form>
          </validation-observer>
        </b-overlay>
      </b-card>
    </div>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from "vee-validate";
import VuexyLogo from "@core/layouts/components/Logo.vue";
import {
  BSpinner,
  BOverlay,
  BLink,
  BFormGroup,
  BFormInput,
  BInputGroupAppend,
  BInputGroup,
  BForm,
  BButton,
  BCard,
} from "bootstrap-vue";
import { required, min, confirmed } from "@validations";
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import store from "@/store/index";
import router from "@/router";
import homeStoreModule from "./homeStoreModule";
import { onUnmounted } from "@vue/composition-api";

export default {
  components: {
    BSpinner,
    BOverlay,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BForm,
    BButton,
    BCard,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],

  setup() {
    const HOME_APP_STORE_MODULE_NAME = "home";
    if (!store.hasModule(HOME_APP_STORE_MODULE_NAME))
      store.registerModule(HOME_APP_STORE_MODULE_NAME, homeStoreModule);
    onUnmounted(() => {
      if (store.hasModule(HOME_APP_STORE_MODULE_NAME))
        store.unregisterModule(HOME_APP_STORE_MODULE_NAME);
    });
  },

  data() {
    return {
      token: this.$route.params.token,
      tokenState: "checking",
      password: "",
      passwordConfirmation: "",
      show: false,
      required,
      min,
      confirmed,
    };
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },

  mounted() {
    localStorage.removeItem("userData");
    localStorage.removeItem("userPerms");

    store
      .dispatch("home/checkPasswordResetToken", { token: this.token })
      .then((response) => {
        this.tokenState = response.data.valid ? "valid" : "invalid";
      })
      .catch(() => {
        this.tokenState = "invalid";
      });
  },

  methods: {
    submit() {
      this.$refs.resetValidation.validate().then((success) => {
        if (!success) return;

        this.show = true;
        store
          .dispatch("home/completePasswordReset", {
            token: this.token,
            password: this.password,
            password_confirmation: this.passwordConfirmation,
          })
          .then((response) => {
            this.show = false;
            if (response.data.success) {
              this.$swal({
                title: "Password changed",
                text: response.data.message,
                icon: "success",
                showCancelButton: false,
                confirmButtonText: "Go to login",
              }).then(() => {
                router.push({ path: "/login" });
              });
            } else {
              this.tokenState = "invalid";
            }
          })
          .catch((error) => {
            this.show = false;
            const res = error.response;
            let text = "Error occurred, please try again or contact support";
            if (res && res.status === 429) {
              text = "Too many attempts. Please wait a minute and try again.";
            } else if (res && res.status === 422 && res.data.errors && res.data.errors.password) {
              text = res.data.errors.password[0];
            }
            this.$swal({
              title: "Error",
              text,
              icon: "error",
              showCancelButton: false,
              confirmButtonText: "OK",
            });
          });
      });
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
